import { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { max, some, includes, map, filter, flatMap } from "lodash"
import {
  Autocomplete,
  Button,
  TextField,
  Popover,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material"
import { Add, Delete } from "@mui/icons-material"
import { FILTER_ON_GROUP } from "helpers/shapes"
import { isFilterValid, useFilterSecuritiesData } from "helpers/filters"
import { OK } from "helpers/data-statuses"
import { SECURITY_IDS } from "./shapes"
import SearchableSelector from "./searchable-selector"
import ValueSelector from "./value-selector"
import SecuritiesSearch from "./securities-search"
import { FilterOption } from "./filter-option"
import { compareSecurity, convertUnifiedSearchItemToFilterItem } from "./helpers"
import "./style.scss"

export const EMPTY_FILTER = [{
  filterOn: null,
  operator: null,
  values: [],
}]

export default function AddFilter({
  filterOns,
  currency,
  heapAddFilterId,
  updateFilters,
  account,
  modelId,
  assetId,
  assetType,
  startDate,
  endDate,
  intraday,
  isExpanded,
  securityIds,
  anchorEl,
  filtersVisible,
  toggleFilters,
  existingFilters = [],
  additionalFilterComponent,
  showAdditionalFilterComponentOnActiveFilterOnly = false,
  forceShowAdditionalFilterComponent = false,
  isApplyButtonDisabledFunction,
}) {
  const [anchorElement, setAnchorElement] = useState(null)
  const [filters, setFilters] = useState(() => existingFilters.length > 0 ? [...existingFilters] : EMPTY_FILTER)
  const [showFilterOnOptionsOnly, setShowFilterOnOptionsOnly] = useState(() => filters.length === 1 && !filters[0].filterOn)
  const [activeSearchIndex, setActiveSearchIndex] = useState(null)

  // If we are using recent instruments and we are within the context of a portfolio or composite asset,
  // we need to make sure the recent instrument exists within that context
  const [filterSecurities, filterSecuritiesStatus] = useFilterSecuritiesData(
    modelId,
    account,
    { id: assetId, type: assetType },
    null,
    null,
    endDate,
    isExpanded,
  )

  let securityFilterIds = securityIds
  // ignore intraday here because the base portfolio will have different securities than the batch
  if (assetId && assetType && !securityIds && filterSecuritiesStatus === OK && !intraday) {
    securityFilterIds = filterSecurities
  }

  const handleSearchSecurities = useCallback((index) => (anchorElement = null) => {
    setAnchorElement(anchorElement)
    setActiveSearchIndex(index)
  }, [setAnchorElement, setActiveSearchIndex])

  const handleCloseSecuritiesSearch = useCallback(() => {
    setAnchorElement(null)
    setActiveSearchIndex(null)
  }, [])

  const handleSetFilterOn = (index, filterOn) => {
    setFilters(currentFilters => currentFilters.map((filter, i) => {
      if (i !== index) return filter
      let operator = null
      if (filterOn?.operators && filterOn.operators?.length > 0) {
        operator = filterOn.operators.find(op => op.value === "in") || filterOn.operators[0]
      }
      return { ...filter, filterOn, operator, values: [] }
    }))
    setShowFilterOnOptionsOnly(false)
  }

  const handleSetOperator = (index, operator) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, operator, values: [] }
        : filter,
    ))
  }

  const handleSetValues = (index, ...newValues) => {
    setFilters(currentFilters => currentFilters.map((filter, i) => {
      if (i !== index) return filter

      const maxLength = max([filter.values.length, newValues.length])
      const newState = []
      for (let j = 0; j < maxLength; j++) {
        newState[j] = newValues[j] === undefined ? filter.values[j] : newValues[j]
      }
      return { ...filter, values: newState }
    }))
  }

  const handleSetAllValues = (index, ...values) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, values }
        : filter,
    ))
  }

  const handleAppendFilterValue = useCallback((index, item) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, values: [...filter.values, item] }
        : filter,
    ))
  }, [])

  const deselectSecurity = useCallback((item) => {
    const { assetClass, exchange, ticker } = item
    setFilters(currentFilters => currentFilters.map((filterObj, i) => i === activeSearchIndex
      ? { ...filterObj, values: filter(filterObj.values, v => {
        if (assetClass === "index") {
          return v.indexId !== ticker
        } else {
          return !(v.ticker === ticker && v.mic === exchange)
        }
      }),
      } : filterObj,
    ))
  }, [activeSearchIndex])

  const deselectIndex = useCallback((id) => {
    setFilters(currentFilters => currentFilters.map((filterObj, i) =>
      i === activeSearchIndex
        ? { ...filterObj, values: filter(filterObj.values, v => v.indexId !== id) }
        : filterObj,
    ))
  }, [activeSearchIndex])

  const disableSearchResult = useCallback(() => (searchResult) => {
    if (!includes(["security", "index"], searchResult.type)) {
      return true
    }
    return false
  }, [])

  const handleSecuritySelected = useCallback((index) => (searchResult) => {
    const currentValues = filters[index]?.values || []
    return some(currentValues, selectedSecurity => {
      if (searchResult.type === "index") {
        return selectedSecurity.indexId === searchResult.id
      } else {
        if (selectedSecurity.assetClass === "index") {
          return selectedSecurity.indexId === searchResult.ticker
        } else {
          return selectedSecurity.ticker === searchResult.ticker && selectedSecurity.mic === searchResult.mic
        }
      }
    })
  }, [filters])

  const itemsActions = useMemo(() => {
    if (activeSearchIndex === null) return {}

    return {
      index: {
        noDispatch: true,
        mainAction: item => () => {
          const { type, id, name } = item
          const currentValues = filters[activeSearchIndex]?.values || []
          const isSelected = some(currentValues, selectedItem => selectedItem.indexId === id)
          if (isSelected) {
            deselectIndex(id)
          } else {
            handleAppendFilterValue(activeSearchIndex, { id, assetClass: type, indexId: id, value: id, label: name })
          }
        },
      },
      security: {
        noDispatch: true,
        mainAction: item => () => {
          const currentValues = filters[activeSearchIndex]?.values || []
          const security = convertUnifiedSearchItemToFilterItem(item)
          const isSelected = some(currentValues, selectedSecurity => compareSecurity(selectedSecurity, security))
          if (isSelected) {
            deselectSecurity(item)
          } else {
            handleAppendFilterValue(activeSearchIndex, security)
          }
        },
      },
    }
  }, [activeSearchIndex, handleAppendFilterValue, deselectSecurity, filters, deselectIndex])

  const handleRemoveFilter = (index) => {
    const noRemainingAppliedFilters = filters[index].isExisting && filters.length === 1
    if (noRemainingAppliedFilters && !forceShowAdditionalFilterComponent) {
      handleRemoveAllFilters()
    } else {
      setFilters(currentFilters => {
        const newFilters = currentFilters.filter((_, i) => i !== index)
        if (newFilters.length === 0) {
          setShowFilterOnOptionsOnly(true)
          return EMPTY_FILTER
        }
        return newFilters
      })
    }
  }

  const handleClearSecurities = (index) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, values: [] }
        : filter,
    ))
  }

  const handleDeleteSecurity = useCallback((index, item) => {
    setFilters(currentFilters => currentFilters.map((filterObj, i) =>
      i === index
        ? { ...filterObj, values: filter(filterObj.values, v => v.assetClass === "index" ? v.indexId !== item.indexId : v.ticker !== item.ticker || v.mic !== item.mic) }
        : filterObj,
    ))
  }, [])

  const handleClose = () => {
    setFilters(EMPTY_FILTER)
    setShowFilterOnOptionsOnly(true)
    toggleFilters()
  }

  const handleAddAnotherFilter = () => {
    setFilters(currentFilters => [...currentFilters, ...EMPTY_FILTER])
  }

  const formattedOptions = useMemo(() => flatMap(filterOns, category => map(category.options, option => ({
    id: option.value,
    label: option.label,
    groupId: category.label,
    groupLabel: category.label,
    order: option.order || 0,
    ...option,
  }))), [filterOns])

  const isAddButtonDisabled = useMemo(() => {
    if (filters.length === 0) return false
    const lastFilter = filters[filters.length - 1]
    return !isFilterValid(lastFilter)
  }, [filters])

  const isApplyButtonDisabled = useMemo(() => {
    const validFilters = filters.filter(filter =>
      filter.filterOn && isFilterValid(filter),
    )
    if (isApplyButtonDisabledFunction) {
      return validFilters.length !== filters.length && isApplyButtonDisabledFunction()
    }
    return validFilters.length !== filters.length
  }, [filters, isApplyButtonDisabledFunction])

  const hasValidFilters = useMemo(() => {
    return filters.some(filter =>
      filter.filterOn && isFilterValid(filter),
    )
  }, [filters])

  const handleRemoveAllFilters = () => {
    updateFilters([])
    handleClose()
  }

  const handleApplyAllFilters = () => {
    updateFilters(filters)
    handleClose()
  }

  const isFilterSelected = useCallback((option) => {
    if (option.type === "longshort") {
      return filters.some(filter =>
        filter.filterOn && filter.filterOn.type === "longshort",
      )
    }
    return filters.some(filter =>
      filter.filterOn && filter.filterOn.value === option.value && filter.filterOn.type === option.type,
    )
  }, [filters])

  if (showFilterOnOptionsOnly && filtersVisible && !forceShowAdditionalFilterComponent) {
    return (
      <SearchableSelector
        items={formattedOptions}
        current={null}
        onSelect={(item) => handleSetFilterOn(0, item)}
        onClickAway={handleClose}
        placeholder="Search..."
        isOpen={filtersVisible}
        anchorElement={anchorEl}
        header="Select a Filter"
        className="filter-on-dropdown"
      />
    )
  }

  return (
    <>
      <Popover
        open={filtersVisible}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
              width: 680,
              maxHeight: "600px",
            },
          },
        }}
      >
        <div className="components_add-filter-new">
          <div className="filter-header sticky-header">
            Filters
          </div>
          <div className="filter-components">
            <div className="filter-content-wrapper">
              <div className="filter-content">
                <Box flexGrow={1} display="flex" flexDirection="column" gap={1}>
                  {filters.map((filter, index) => {
                    const isUnsupported = filter.isUnsupported
                    const filterRow = (
                      <Box
                        display="flex"
                        gap={1.5}
                        key={`${index}-${filter.id}`}
                        className={classNames("filter-content-group", { "unsupported-filter": isUnsupported })}
                      >
                        <Box width={200} className="filter-on">
                          <Autocomplete
                            options={formattedOptions}
                            value={filter.filterOn}
                            renderOption={({ key, ...props }, option) => <FilterOption key={key} {...props} option={option} disabled={isFilterSelected(option) || option.isUnsupported} />}
                            blurOnSelect
                            clearOnBlur
                            disableClearable
                            disabled={isUnsupported}
                            isOptionEqualToValue={(option, value) => option.value === value.value && option.type === value.type}
                            groupBy={option => option.groupLabel}
                            getOptionKey={(option) => `${option.groupLabel} - ${option.value}`}
                            onChange={(_, filterOn) => handleSetFilterOn(index, filterOn)}
                            getOptionDisabled={(option) => isFilterSelected(option) || option.isUnsupported}
                            renderInput={(params) => <TextField
                              {...params}
                              variant="standard"
                              placeholder="Select a metric or descriptor..."
                              fullWidth
                            />}
                            className="filter-control filter-on"
                            slotProps={{
                              paper: {
                                sx: {
                                  minWidth: "fit-content",
                                  width: "auto",
                                },
                              },
                            }}
                          />
                        </Box>
                        <Box width={200} className="filter-operator">
                          {filter.filterOn && filter.filterOn.operators && (
                            <Autocomplete
                              options={filter.filterOn.operators}
                              value={filter.operator}
                              blurOnSelect
                              clearOnBlur
                              disableClearable
                              disabled={isUnsupported}
                              isOptionEqualToValue={(option, value) => option.value === value.value}
                              groupBy={option => option.category}
                              getOptionKey={(option) => `${option.category} - ${option.value}`}
                              onChange={(_, operator) => handleSetOperator(index, operator)}
                              renderInput={(params) => <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                              />}
                              className="filter-control"
                            />
                          )}
                        </Box>
                        <Box width={200} className="filter-values">
                          <ValueSelector
                            filterOn={filter.filterOn}
                            operator={filter.operator}
                            values={filter.values}
                            setValues={(...values) => handleSetValues(index, ...values)}
                            setAllValues={(...values) => handleSetAllValues(index, ...values)}
                            searchSecurities={handleSearchSecurities(index)}
                            clearSecurities={() => handleClearSecurities(index)}
                            handleDelete={(item) => handleDeleteSecurity(index, item)}
                            currency={currency}
                            className="filter-control"
                            disabled={isUnsupported}
                          />
                        </Box>
                        <div>
                          <IconButton onClick={() => handleRemoveFilter(index)} className="remove-filter-btn">
                            <Delete />
                          </IconButton>
                        </div>
                      </Box>
                    )

                    return isUnsupported ? (
                      <Tooltip key={`${index}-${filter.id}`} title="The filter could not be found" arrow>
                        {filterRow}
                      </Tooltip>
                    ) : filterRow
                  })}
                </Box>
              </div>
            </div>
            <div className={classNames("add-another-wrapper", { hidden: isAddButtonDisabled })}>
              <Button
                onClick={handleAddAnotherFilter}
                variant="text"
                color="primary"
                startIcon={<Add />}
                className="add-another-btn"
              >
                Add another filter
              </Button>
            </div>
            <div className="secondary-filters" onClick={e => e.stopPropagation()}>
              {(showAdditionalFilterComponentOnActiveFilterOnly && hasValidFilters) || forceShowAdditionalFilterComponent
                ? additionalFilterComponent
                : null}
            </div>
          </div>
          <div className="filter-actions">
            <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
              <Button className="cancel-btn" onClick={handleClose} variant="contained">Cancel</Button>
              <Button
                className="apply-btn"
                disabled={isApplyButtonDisabled}
                onClick={handleApplyAllFilters}
                variant="contained"
                color="primary"
                id={heapAddFilterId}
              >
                Apply
              </Button>
            </Box>
          </div>
        </div>
      </Popover>
      <Popover
        className="securities-search-popover"
        onClose={handleCloseSecuritiesSearch}
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
              minHeight: 0,
            },
          },
        }}
      >
        <div className={classNames("securities-search-wrapper", { hidden: !anchorElement })}>
          <SecuritiesSearch
            account={account}
            modelId={modelId}
            assetId={assetId}
            assetType={assetType}
            itemsActions={itemsActions}
            disableSearchResult={disableSearchResult(activeSearchIndex)}
            securitySelected={handleSecuritySelected(activeSearchIndex)}
            startDate={startDate}
            endDate={endDate}
            intraday={intraday}
            isExpanded={isExpanded}
            securityIds={securityFilterIds}
          />
        </div>
      </Popover>
    </>
  )
}

AddFilter.propTypes = {
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  addFilter: PropTypes.func.isRequired,
  heapAddFilterId: PropTypes.string.isRequired,
  account: PropTypes.string,
  modelId: PropTypes.string,
  assetId: PropTypes.string,
  assetType: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  intraday: PropTypes.bool,
  isExpanded: PropTypes.bool,
  currency: PropTypes.string,
  securityIds: SECURITY_IDS,
  anchorEl: PropTypes.object,
  filtersVisible: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  removeFilter: PropTypes.func,
  editFilter: PropTypes.func,
  existingFilters: PropTypes.array,
  additionalFilterComponent: PropTypes.node,
  updateFilters: PropTypes.func,
  showAdditionalFilterComponentOnActiveFilterOnly: PropTypes.bool,
  forceShowAdditionalFilterComponent: PropTypes.bool,
  isApplyButtonDisabledFunction: PropTypes.func,
}
